import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';

import { UseOutletContextPayroll } from './Payroll';
import EmployeeFormFields from './EmployeeFormFields';
import PayrollAccessDenied from './PayrollAccessDenied';
import { formatAvsNumber } from '../../../scripts/avs.validation';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { Employee } from '../../../models/employee.model';
import { employeeSchema, EmployeeFormData } from '../../../schemas/employee.schema';
import { configureZodErrors } from '../../../schemas/zod-error-map';
import AppPreloader from '../../../components/Preloader';

const EmployeeEditPage: React.FunctionComponent = () => {
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();
  const navigate = useNavigate();
  const { id } = useParams();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [refreshingSnapshot, setRefreshingSnapshot] = useState(false);
  const [expenseAccess, setExpenseAccess] = useState<string>('auto');
  const [expenseAccessInitial, setExpenseAccessInitial] = useState<string>('auto');

  configureZodErrors(lang.labels);

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    setRoute({path: {root: lang.labels.payroll, branch: lang.labels.editEmployee}, company: false})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser && id) {
      loadEmployee(companyForUser.id, id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id]);

  const loadEmployee = async (id_company: string, id_employee: string) => {
    setLoading(true);
    const response = await EmployeeDA.getEmployee(id_company, id_employee);

    if (response.status === 200 && response.data) {
      const emp: Employee = response.data;
      setEmployee(emp);

      const eaValue = emp.expense_access || 'auto';
      setExpenseAccess(eaValue);
      setExpenseAccessInitial(eaValue);

      // Piège #1: reset() with API data so uncontrolled fields are populated
      reset({
        id_contact: emp.id_contact || '',
        contact_snapshot: {
          name: emp.contact_snapshot?.name || `${emp.contact_snapshot?.first_name ?? ''} ${emp.contact_snapshot?.last_name ?? ''}`.trim(),
          first_name: emp.contact_snapshot?.first_name || null,
          last_name: emp.contact_snapshot?.last_name || null,
          address: {
            street: emp.contact_snapshot?.address?.street || '',
            number: emp.contact_snapshot?.address?.number || '',
            complement: emp.contact_snapshot?.address?.complement || null,
            zip: emp.contact_snapshot?.address?.zip || '',
            city: emp.contact_snapshot?.address?.city || '',
            country: emp.contact_snapshot?.address?.country || 'CH',
          },
          email: emp.contact_snapshot?.email || null,
          phone: emp.contact_snapshot?.phone || null,
          language: emp.contact_snapshot?.language || 'fr',
        },
        avs_number: emp.avs_number ? formatAvsNumber(emp.avs_number) : '',
        birth_date: emp.birth_date ? emp.birth_date.slice(0, 10) : '',
        gender: (emp.gender as any) || undefined,
        civil_status: (emp.civil_status as any) || undefined,
        civil_status_since: emp.civil_status_since ? emp.civil_status_since.slice(0, 10) : '',
        nationality: emp.nationality || '',
        permit: {
          type: (emp.permit?.type as any) || 'CH',
          number: emp.permit?.number || '',
          valid_until: emp.permit?.valid_until ? emp.permit.valid_until.slice(0, 10) : '',
          issuing_canton: emp.permit?.issuing_canton || '',
        },
        residence: {
          canton: emp.residence?.canton || '',
          municipality: emp.residence?.municipality || '',
          municipality_bfs: emp.residence?.municipality_bfs || '',
          country: emp.residence?.country || 'CH',
        },
        work_canton: emp.work_canton || '',
        spouse: {
          has_spouse: emp.spouse?.has_spouse || false,
          is_working: emp.spouse?.is_working || false,
          work_canton: emp.spouse?.work_canton || '',
          work_country: emp.spouse?.work_country || '',
        },
        children: (emp.children || []).map(c => ({
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          birth_date: c.birth_date ? c.birth_date.slice(0, 10) : '',
          allowance_from: c.allowance_from ? c.allowance_from.slice(0, 10) : '',
          allowance_to: c.allowance_to ? c.allowance_to.slice(0, 10) : '',
          in_education: c.in_education || false,
        })),
        payment: {
          iban: emp.payment?.iban || '',
          bic: emp.payment?.bic || '',
          holder_name: emp.payment?.holder_name || '',
        },
        notes: emp.notes || '',
      });
    } else if (response.status === 403) {
      setAccessDenied(true);
    }
    setLoading(false);
  };

  const refreshContactSnapshot = async () => {
    if (!companyForUser || !employee?.id_contact) return;
    setRefreshingSnapshot(true);

    const response = await ApiDigitalAdminContact.getContact(companyForUser.id, employee.id_contact);
    if (response.status === 200 && response.data) {
      const d = response.data.data ?? response.data;
      const displayName = d.first_name && d.last_name
        ? `${d.first_name} ${d.last_name}`
        : d.name || '';

      setValue('contact_snapshot', {
        name: displayName,
        first_name: d.first_name || null,
        last_name: d.last_name || null,
        address: {
          street: d.address?.street || '',
          number: d.address?.number || '',
          complement: d.address?.additional || null,
          zip: d.address?.zip_code || '',
          city: d.address?.city || '',
          country: d.address?.country || 'CH',
        },
        email: d.email || null,
        phone: d.phone || null,
        language: d.language || 'fr',
      });
    }
    setRefreshingSnapshot(false);
  };

  const onSubmit = async (data: any) => {
    if (!companyForUser || !id) return;
    setSubmitting(true);

    const response = await EmployeeDA.putEmployee(companyForUser.id, id, data);

    if (response.status === 200) {
      if (expenseAccess !== expenseAccessInitial) {
        const eaResponse = await EmployeeDA.patchExpenseAccess(companyForUser.id, id, expenseAccess);
        if (eaResponse.status !== 200) {
          Swal.fire({
            title: lang.labels.error || 'Error',
            text: eaResponse.data?.message || eaResponse.message || 'An error occurred',
            icon: 'error',
          });
          setSubmitting(false);
          return;
        }
      }
      Swal.fire({
        title: lang.labels.successfullyUpdated || 'Updated successfully',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
      navigate(`/main/payroll/employees/view/${id}`);
    } else {
      Swal.fire({
        title: lang.labels.error || 'Error',
        text: response.data?.message || response.message || 'An error occurred',
        icon: 'error',
      });
    }

    setSubmitting(false);
  };

  if (accessDenied) return <PayrollAccessDenied />;

  if (loading) {
    return <AppPreloader />;
  }

  if (!employee) {
    return (
      <div className="card">
        <div className="card-body text-center py-10">
          <p className="text-muted fw-bold fs-6">{lang.labels.noEmployeesFound}</p>
          <Link to="/main/payroll/employees/list" className="btn btn-primary mt-3">
            {lang.labels.backToList}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">
          <h3 className="fw-bolder">{lang.labels.editEmployee}</h3>
        </div>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Contact read-only */}
          <h4 className="fw-bolder mb-5">{lang.labels.linkedContact || 'Linked contact'}</h4>
          <div className="d-flex align-items-center bg-light-primary rounded p-4 mb-5">
            <div className="flex-grow-1">
              <span className="fw-bold fs-6">
                {employee.contact_snapshot?.first_name} {employee.contact_snapshot?.last_name}
              </span>
              {employee.contact_snapshot?.email && (
                <span className="text-muted ms-2">({employee.contact_snapshot.email})</span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={refreshContactSnapshot}
              disabled={refreshingSnapshot}
            >
              {refreshingSnapshot ? (
                <span className="spinner-border spinner-border-sm me-1"></span>
              ) : (
                <i className="bi bi-arrow-clockwise me-1"></i>
              )}
              {lang.labels.refreshSnapshot || 'Refresh contact data'}
            </button>
          </div>

          <div className="separator my-5"></div>

          <EmployeeFormFields control={control} watch={watch} lang={lang} isEdit />

          {/* Expense access */}
          <div className="separator my-5"></div>
          <h4 className="fw-bolder mb-5">{lang.labels.expenseAccess}</h4>
          <div className="row">
            <div className="col-md-6">
              <div className="fv-row mb-5">
                <label className="form-label">{lang.labels.expenseAccess}</label>
                <select
                  className="form-select"
                  value={expenseAccess}
                  onChange={(e) => setExpenseAccess(e.target.value)}
                >
                  <option value="auto">{lang.labels.expenseAccessAuto}</option>
                  <option value="forced_on">{lang.labels.expenseAccessForcedOn}</option>
                  <option value="forced_off">{lang.labels.expenseAccessForcedOff}</option>
                </select>
                <div className="form-text text-muted mt-1" style={{ fontSize: '0.85em' }}>
                  {lang.labels.expenseAccessHelp}
                </div>
              </div>
            </div>
          </div>

          <div className="separator my-5"></div>
          <div className="d-flex justify-content-end">
            <Link to={`/main/payroll/employees/view/${id}`} className="btn btn-light me-3">
              {lang.labels.cancel || 'Cancel'}
            </Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : null}
              {lang.labels.save || 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeEditPage;
